import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, ExternalLinkIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { AudioMaster } from '../../components/AudioMaster';
import { StatusTag } from '../../components/StatusTag';
import { VideoStage } from '../../components/VideoStage';
import { clientStatusLabel, deliverableStage, isReviewable, metaLabel } from '../../utils/deliverables';
type Panel = 'idle' | 'confirm' | 'approved';
const EASE = [0.23, 1, 0.32, 1] as const;
export function EpisodeReview() {
  const {
    activeDeliverable,
    reviewDeliverables,
    approveDeliverable,
    requestChanges,
    goClient,
    notify,
    canApprove,
    canComment,
    projectComplete
  } = usePortal();
  const [panel, setPanel] = useState<Panel>('idle');
  const [composerOpen, setComposerOpen] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const [timecode, setTimecode] = useState('');

  /**
   * A change-request draft loses its home if another reviewer approves the
   * deliverable while the composer is open — the composer's render condition
   * (canAddChanges) goes false the moment the status flips, and the form
   * simply unmounts. Rather than let that happen silently, this catches the
   * settled transition and says what happened, instead of the draft just
   * vanishing.
   */
  const settledTrackRef = useRef<{
    id: string;
    settled: boolean;
  }>({
    id: '',
    settled: false
  });
  useEffect(() => {
    if (!activeDeliverable) return;
    const nowSettled = activeDeliverable.status === 'Approved' || activeDeliverable.status === 'Delivered';
    const track = settledTrackRef.current;
    const sameItem = track.id === activeDeliverable.id;
    if (sameItem && nowSettled && !track.settled && composerOpen && (changeNote.trim() || timecode.trim())) {
      notify('This version was approved before your change request was sent — your draft was not submitted.');
      setComposerOpen(false);
      setChangeNote('');
      setTimecode('');
    }
    settledTrackRef.current = {
      id: activeDeliverable.id,
      settled: nowSettled
    };
  }, [activeDeliverable, composerOpen, changeNote, timecode, notify]);
  if (!activeDeliverable) return null;
  const item = activeDeliverable;
  const index = reviewDeliverables.filter(entry => deliverableStage(entry.status) === deliverableStage(item.status)).findIndex(entry => entry.id === item.id);
  const number = index >= 0 ? String(index + 1).padStart(2, '0') : '';
  const inReview = isReviewable(item.status);
  const settled = item.status === 'Approved' || item.status === 'Delivered';
  const changesRequested = item.status === 'Changes Requested';
  const showVideo = item.type === 'Video' || item.type === 'Other' || item.type === 'Folder';
  const showAudio = item.type === 'Audio' || Boolean(item.audioUrl);

  /** Changes stay open for as long as this version is the current cut. */
  const canAddChanges = canComment && !projectComplete && !settled && (inReview || changesRequested);
  const currentChanges = item.changeRequests.filter(request => request.version === item.version);
  function confirmApproval() {
    approveDeliverable(item.id);
    setPanel('approved');
    setComposerOpen(false);
    notify('Portal notified');
  }
  function addChange(event: React.FormEvent) {
    event.preventDefault();
    const body = changeNote.trim();
    if (!body) return;
    requestChanges(item.id, body, timecode);
    setChangeNote('');
    setTimecode('');
    notify('Change added · Portal has been notified');
  }
  return <div data-id="element-501" data-mp="3ej6dkp1cnyr0">
      <button type="button" onClick={() => goClient('episodes')} className="label group flex items-center gap-3 text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-502" data-mp="3ejpnbigujgty">
        <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-503" data-mp="233rk3v8cnp0n" />
        All Deliverables
      </button>

      <header className="mt-10 border-b border-rule pb-10" data-id="element-504" data-mp="3vrpceiglplu5">
        <p className="label text-mute" data-id="element-505" data-mp="3h2jces9xvrl7">{metaLabel(item)}</p>
        <h1 className="mt-6 font-display text-4xl leading-[0.95] tracking-tight sm:text-7xl" data-id="element-506" data-mp="3h4466fofj6i0">
          {number && `${number} — `}
          {item.name.toUpperCase()}
        </h1>
        <div className="mt-8 flex flex-wrap items-center gap-x-10 gap-y-4" data-id="element-507" data-mp="dmud65ocbevf">
          <StatusTag status={item.status} audience="review" data-id="element-508" data-mp="ilfek8fbw4lg" />
          {item.version && <p className="label m-0 text-mute" data-id="element-509" data-mp="4l4nb4a3wnzr">Version {item.version}</p>}
          {item.versionDate !== '—' && <p className="label m-0 text-mute" data-id="element-510" data-mp="8mpvicbp4e3m">{item.versionDate}</p>}
          {item.runtime !== '—' && <p className="label m-0 text-mute" data-id="element-511" data-mp="8okirxafwiav">Runtime {item.runtime}</p>}
        </div>
      </header>

      <div className="mt-12 grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16" data-id="element-512" data-mp="2pdpvstfmkcb3">
        <div className="lg:col-span-8" data-id="element-513" data-mp="2tauduu6iy6vt">
          {showVideo && <>
              <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-rule pb-4" data-id="element-514" data-mp="2lruw0syponek">
                <h2 className="label m-0 text-graphite" data-id="element-515" data-mp="2gu31dr6omn6o">Latest Review Version</h2>
                <p className="label-sm m-0 text-mute" data-id="element-516" data-mp="2vr3mvcya5lwp">
                  {item.version ? `${item.version} · ` : ''}Plays inside Portal
                </p>
              </div>
              <div className="mt-6" data-id="element-517" data-mp="232554gy2klrx">
                <VideoStage driveUrl={item.driveUrl} label={`${item.name} — ${item.version || 'Review copy'}`} runtime={item.runtime} format={item.videoFormat} data-id="element-518" data-mp="2337rcbkg601i" />
              </div>
            </>}

          {!showVideo && !showAudio && <div className="border border-rule px-7 py-9" data-id="element-519" data-mp="1kc6xg1y6ouot">
              <p className="label m-0 text-graphite" data-id="element-520" data-mp="1kam2wcbvdbqy">Latest Review Version</p>
              <p className="mt-5 font-display text-2xl" data-id="element-521" data-mp="1fckjfqaj7aey">{item.name}</p>
              {settled ? <p className="label mt-6 text-mute" data-id="element-522" data-mp="250pykmorcos7">{clientStatusLabel(item.status)}</p> : <button type="button" onClick={() => notify('Simulated · would open in Google Drive')} className="label group mt-8 flex items-center gap-3 border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-523" data-mp="291jmvh6445lm">
                  Open for Review
                  <ExternalLinkIcon className="h-3.5 w-3.5" strokeWidth={1.25} data-id="element-524" data-mp="1q3p6uj6kgttq" />
                </button>}
            </div>}

          <div className="mt-12" data-id="element-525" data-mp="1v20gotwv34by">
            <AnimatePresence mode="wait" initial={false} data-id="element-526" data-mp="2024ycn9ia53r">
              {panel === 'approved' || settled && panel === 'idle' ? <motion.div key="approved" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              ease: EASE
            }} className="border border-ink px-7 py-9 sm:px-10" data-id="element-527" data-mp="36hp1bemn7r9l">
                  <p className="flex items-center gap-4 font-display text-3xl leading-none sm:text-4xl" data-id="element-528" data-mp="uwg89ugedcs7">
                    {item.status === 'Approved' ? 'Approved' : item.status}
                    <CheckIcon className="h-6 w-6" strokeWidth={1} data-id="element-529" data-mp="l4af4heespbf" />
                  </p>
                  <p className="label mt-5 text-mute" data-id="element-530" data-mp="h40954qm9ywk">
                    {item.status === 'Approved' ? `${item.version ? `${item.version} approved · ` : ''}Portal has been notified.` : 'Your final files are ready to download.'}
                  </p>
                  <button type="button" onClick={() => goClient('files')} className="label group mt-8 flex items-center gap-3 border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-531" data-mp="26zjr7vweg19">
                    Go to Files
                    <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-532" data-mp="15fwplgyfj5fh" />
                  </button>
                </motion.div> : projectComplete ? <motion.div key="complete" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              ease: EASE
            }} className="border border-ink px-7 py-9 sm:px-10" data-id="element-533" data-mp="10mlideppgmn1">
                  <p className="font-display text-3xl leading-none sm:text-4xl" data-id="element-534" data-mp="dq1q4rguxzd3">Delivered</p>
                  <p className="mt-5 max-w-lg font-display text-lg text-charcoal" data-id="element-535" data-mp="hptxj0uilzgv">
                    This project is complete. Final files are available to download.
                  </p>
                  <button type="button" onClick={() => goClient('files')} className="label group mt-8 flex items-center gap-3 border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-536" data-mp="2c2s87lk4my4q">
                    Go to Files
                    <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-537" data-mp="5fktya49xwt0" />
                  </button>
                </motion.div> : panel === 'confirm' ? <motion.div key="confirm" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              ease: EASE
            }} className="border border-ink px-7 py-9 sm:px-10" data-id="element-538" data-mp="t5f6srolfld9">
                  <h2 className="font-display text-2xl leading-tight sm:text-3xl" data-id="element-539" data-mp="2pxp69v6o5l0t">
                    Approve {item.version ? `version ${item.version}` : item.name}?
                  </h2>
                  <p className="mt-5 max-w-lg font-display text-lg text-charcoal" data-id="element-540" data-mp="1vc0pdh18r9ys">
                    Portal will be notified and will prepare your final deliverables.
                  </p>
                  <div className="mt-8 flex flex-wrap items-center gap-6" data-id="element-541" data-mp="1mc3u2wkcjcur">
                    <button type="button" onClick={confirmApproval} className="label border border-ink bg-ink px-7 py-4 text-paper transition-colors duration-150 ease-out hover:bg-charcoal" data-id="element-542" data-mp="1mdx0qbuylx2r">
                      Confirm Approval
                    </button>
                    <button type="button" onClick={() => setPanel('idle')} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-543" data-mp="25bhsprt0tbrl">
                      Cancel
                    </button>
                  </div>
                </motion.div> : canApprove || canAddChanges ? <motion.div key="actions" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.2,
              ease: EASE
            }} data-id="element-544" data-mp="3aao5zx9eq2wl">
                  <div className="flex flex-wrap gap-5" data-id="element-545" data-mp="3jbulq448f9os">
                    {canApprove && !projectComplete && !settled && <button type="button" onClick={() => setPanel('confirm')} className="label flex-1 border border-ink bg-ink px-8 py-5 text-paper transition-colors duration-150 ease-out hover:bg-charcoal sm:flex-none" data-id="element-546" data-mp="3o7joshfgn8pu">
                        Approve
                      </button>}
                    {canAddChanges && <button type="button" onClick={() => setComposerOpen(true)} className="label flex-1 border border-ink px-8 py-5 text-ink transition-colors duration-150 ease-out hover:bg-ink hover:text-paper sm:flex-none" data-id="element-547" data-mp="fum85wgjevzv">
                        {currentChanges.length > 0 ? 'Add Another Change' : 'Request Changes'}
                      </button>}
                  </div>

                  {changesRequested && <p className="label mt-6 text-mute" data-id="element-548" data-mp="2vmt2ey79srwt">
                      Changes requested · Portal has been notified
                    </p>}
                </motion.div> : inReview ? <motion.p key="viewonly" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.2,
              ease: EASE
            }} className="label m-0 border border-rule px-7 py-8 text-mute" data-id="element-549" data-mp="2zma5jkz85cyt">
                  Your access is view only · approvals sit with your team&rsquo;s approver
                </motion.p> : <motion.p key="editing" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.2,
              ease: EASE
            }} className="label m-0 border border-rule px-7 py-8 text-mute" data-id="element-550" data-mp="3ltdzg7bl49pv">
                  This is with Portal · nothing to review yet
                </motion.p>}
            </AnimatePresence>
          </div>

          {composerOpen && canAddChanges && <form onSubmit={addChange} className="mt-8 border border-ink px-7 py-9 sm:px-10" data-id="element-551" data-mp="2p5riziuwp1y2">
              <h2 className="font-display text-2xl leading-tight sm:text-3xl" data-id="element-552" data-mp="2t72yuwnza8t2">
                What would you like us to change?
              </h2>
              <div className="mt-8 flex flex-col gap-8 sm:flex-row sm:items-start" data-id="element-553" data-mp="nb8shojiqab1">
                <label className="sm:w-32" data-id="element-554" data-mp="rckihv6b0xjr">
                  <span className="label block text-graphite" data-id="element-555" data-mp="2ok783pczi4v">Timecode</span>
                  <input value={timecode} onChange={event => setTimecode(event.target.value)} placeholder="e.g. 00:17" inputMode="numeric" className="mt-4 w-full border-0 border-b border-rule bg-transparent pb-3 font-display text-xl text-ink placeholder:text-mute focus:border-ink focus:outline-none" data-id="element-556" data-mp="3tubvsynhmmpk" />
                </label>
                <label className="flex-1" data-id="element-557" data-mp="3b32brvg3cfjq">
                  <span className="label block text-graphite" data-id="element-558" data-mp="3b1zorylw2x93">Change Request</span>
                  <textarea value={changeNote} onChange={event => setChangeNote(event.target.value)} rows={3} placeholder="Describe the change required…" className="mt-4 w-full resize-none border-0 border-b border-rule bg-transparent pb-3 font-display text-xl text-ink placeholder:text-mute focus:border-ink focus:outline-none" data-id="element-559" data-mp="362n0raja7k4x" />
                </label>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-6" data-id="element-560" data-mp="1bmppftrh861b">
                <button type="submit" className="label border border-ink px-7 py-4 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-561" data-mp="1gonbevr9hexy">
                  Add Change
                </button>
                <button type="button" onClick={() => setComposerOpen(false)} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-562" data-mp="sxrrekt1ro19">
                  Done
                </button>
              </div>
            </form>}

          {currentChanges.length > 0 && <section aria-labelledby="requested-changes" className="mt-16" data-id="element-563" data-mp="56cj8belaci3">
              <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-rule pb-4" data-id="element-564" data-mp="a5ywgkuf3sqw">
                <h2 id="requested-changes" className="label m-0 text-graphite" data-id="element-565" data-mp="2513auh3kfp3">
                  Requested Changes{item.version ? ` · ${item.version}` : ''}
                </h2>
                <p className="label-sm m-0 text-mute" data-id="element-566" data-mp="bvc78h0s6ovz">
                  {currentChanges.length} change{currentChanges.length === 1 ? '' : 's'}
                </p>
              </div>
              <ul className="m-0 list-none p-0" data-id="element-567" data-mp="3fcm26wkfkql7">
                {currentChanges.map(request => <li key={request.id} className="border-b border-rule py-6" data-id="element-568" data-mp="3bcu092pjliot">
                    <div className="flex flex-wrap items-baseline gap-3" data-id="element-569" data-mp="3pc3370oqmgau">
                      <span className="label text-ink" data-id="element-570" data-mp="3p977zk49y5q3">
                        {request.timecode ? `${request.timecode} · ` : ''}
                        {request.authorName}
                      </span>
                      {/* Closes the gap between a request and the next version. */}
                      {request.resolved && <span className="label-sm text-graphite" data-id="element-571" data-mp="2hjl7kathit3s">✓ Completed by Portal</span>}
                    </div>
                    <p className="mt-2 max-w-2xl font-display text-xl leading-snug text-charcoal" data-id="element-572" data-mp="2mk8wmqy5kzvd">
                      {request.body}
                    </p>
                    <p className="label-sm mt-3 text-mute" data-id="element-573" data-mp="2djat09rmmudb">Sent {request.time}</p>
                  </li>)}
              </ul>
            </section>}

          {item.versions.some(entry => entry.visibleToClient) && <section aria-labelledby="previous-versions" className="mt-16" data-id="element-574" data-mp="35bjll2kbj2km">
              <h2 id="previous-versions" className="label border-b border-rule pb-4 text-graphite" data-id="element-575" data-mp="2rhiwz1n2yvxz">
                Previous Versions
              </h2>
              <ul className="m-0 list-none p-0" data-id="element-576" data-mp="3k551cc44u4lw">
                {item.versions.filter(entry => entry.visibleToClient).map(entry => <li key={entry.id} className="flex flex-wrap items-baseline justify-between gap-4 border-b border-rule py-5" data-id="element-577" data-mp="1ow8aij33xgob">
                      <span className="flex items-baseline gap-5" data-id="element-578" data-mp="1f6f21qy9hhoa">
                        <span className="font-display text-2xl leading-none" data-id="element-579" data-mp="1a6k9zk2r2m1n">{entry.version}</span>
                        <span className="label-sm text-mute" data-id="element-580" data-mp="27tsr72vpx8xu">
                          {entry.label} · {entry.date}
                          {entry.changeCount ? ` · ${entry.changeCount} change${entry.changeCount === 1 ? '' : 's'}` : ''}
                        </span>
                      </span>
                      <button type="button" onClick={() => notify(`Simulated · would play ${entry.version}`)} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-581" data-mp="1ttqf5xa3d9qi">
                        Watch
                      </button>
                    </li>)}
              </ul>
            </section>}

          {showAudio && <section aria-labelledby="audio-review" className="mt-16" data-id="element-582" data-mp="3323dowo056yi">
              <h2 id="audio-review" className="label border-b border-rule pb-4 text-graphite" data-id="element-583" data-mp="37tl3qpohln4p">
                Audio Review
              </h2>
              <AudioMaster label={`${item.name} — Audio ${item.version}`} driveUrl={item.audioUrl} data-id="element-584" data-mp="3cs64cq7ni8to" />
            </section>}

        </div>

        <aside className="lg:col-span-4 lg:border-l lg:border-rule lg:pl-12" data-id="element-585" data-mp="3luxjp13ndoql">
          <h2 className="label border-b border-rule pb-4 text-graphite" data-id="element-586" data-mp="3qsz0ffdlpbyw">How Review Works</h2>
          <ol className="m-0 list-none p-0" data-id="element-587" data-mp="1rg4m4wj7enw0">
            {[{
            step: '01',
            title: 'Watch',
            body: 'The latest version plays here inside Portal.'
          }, {
            step: '02',
            title: 'Respond',
            body: 'Add as many timecoded changes as you need.'
          }, {
            step: '03',
            title: 'Approve',
            body: 'Confirm the version once you are happy.'
          }, {
            step: '04',
            title: 'Download',
            body: 'Final deliverables move to Files after approval.'
          }].map(step => <li key={step.step} className="border-b border-rule py-6" data-id="element-588" data-mp="1yxntlfm5s5ax">
                <div className="flex items-baseline gap-4" data-id="element-589" data-mp="23x0psqyx6oq6">
                  <span className="label text-mute" data-id="element-590" data-mp="23zmxcqlt948e">{step.step}</span>
                  <span className="font-display text-xl leading-none" data-id="element-591" data-mp="28xy58vsjp4nz">{step.title}</span>
                </div>
                <p className="mt-3 font-display text-base leading-snug text-graphite" data-id="element-592" data-mp="2dv7wmgzhvqeg">{step.body}</p>
              </li>)}
          </ol>
          <p className="label-sm mt-7 leading-relaxed text-mute" data-id="element-593" data-mp="jhf7xg8onulv">
            Review copies stream from Google Drive · finals are delivered in Files
          </p>
        </aside>
      </div>
    </div>;
}